/**
 * HotelRequestDetailPage - Dettaglio richiesta con thread commenti e
 * transizioni di stato dalla sidebar.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Loader2, ArrowLeft, ExternalLink, MessageSquare, CheckCircle2, Archive } from 'lucide-react';

const STATUS_ACTIONS = [
  { key: 'presa_in_carico', label: 'Prendi in carico', color: 'bg-blue-600 hover:bg-blue-700 text-white' },
  { key: 'fornitore_assegnato', label: 'Fornitore assegnato', color: 'bg-amber-600 hover:bg-amber-700 text-white' },
  { key: 'in_lavorazione', label: 'In lavorazione', color: 'bg-amber-500 hover:bg-amber-600 text-white' },
  { key: 'completata', label: 'Segna completata', color: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
];

const fmtDT = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const HotelRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/hotel-requests/${id}`);
      setReq(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento');
      navigate('/richieste');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try {
      setSaving(true);
      const { data } = await api.patch(`/hotel-requests/${id}`, { status });
      setReq(data);
      toast.success('Stato aggiornato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento');
    } finally { setSaving(false); }
  };

  const archive = async () => {
    if (!window.confirm('Archiviare questa richiesta?')) return;
    try {
      setSaving(true);
      await api.delete(`/hotel-requests/${id}`);
      toast.success('Archiviata');
      navigate('/richieste');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore archiviazione');
    } finally { setSaving(false); }
  };

  const postComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    try {
      setSaving(true);
      await api.post(`/hotel-requests/${id}/comments`, { text });
      setNewComment('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio commento');
    } finally { setSaving(false); }
  };

  if (loading || !req) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4 max-w-6xl" data-testid="request-detail-page">
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Link to="/richieste" className="hover:text-slate-800 dark:hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Inbox Richieste</Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Corpo */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10px] uppercase">{req.type?.replace('_', ' ')}</Badge>
                    <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10px] uppercase">{req.status?.replace('_', ' ')}</Badge>
                    <Badge variant="outline" className="text-[10px]">{req.priority?.toUpperCase()}</Badge>
                  </div>
                  <h1 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{req.title}</h1>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {req.hotel_name} · aperta da {req.created_by_name} · {fmtDT(req.created_at)}
                  </p>
                </div>
                {req.type === 'acquisto' && req.total > 0 && (
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">€ {req.total.toFixed(2)}</p>
                )}
              </div>

              {req.description && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-3 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-line leading-relaxed">
                  {req.description}
                </div>
              )}

              {req.type === 'acquisto' && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500 dark:text-slate-400">Fornitore:</span> <span className="font-medium">{req.supplier_suggested || '—'}</span></div>
                  <div><span className="text-slate-500 dark:text-slate-400">Prezzo:</span> <span className="font-medium">€ {(req.unit_price || 0).toFixed(2)} × {req.quantity || 1}</span></div>
                  {req.product_link && (
                    <a href={req.product_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline col-span-2 truncate">🔗 {req.product_link}</a>
                  )}
                </div>
              )}
              {req.type === 'fornitore_extra' && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500 dark:text-slate-400">Ditta:</span> <span className="font-medium">{req.supplier_name || '—'}</span></div>
                  <div><span className="text-slate-500 dark:text-slate-400">Telefono:</span> <span className="font-medium">{req.supplier_phone || '—'}</span></div>
                  <div><span className="text-slate-500 dark:text-slate-400">Preventivo:</span> <span className="font-medium">€ {(req.estimated_cost || 0).toFixed(2)}</span></div>
                  <div><span className="text-slate-500 dark:text-slate-400">Programmato:</span> <span className="font-medium">{req.scheduled_intervention_date ? fmtDT(req.scheduled_intervention_date) : '—'}</span></div>
                </div>
              )}

              {(req.photos || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-2">
                  {req.photos.map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noreferrer" className="block">
                      <img src={p} alt="" className="w-24 h-24 object-cover rounded-lg" />
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Thread commenti */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><MessageSquare className="w-4 h-4" /> Thread ({(req.comments || []).length})</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-3" data-testid="comments-thread">
              {(req.comments || []).length === 0 && (
                <p className="text-xs text-slate-400 italic">Nessun commento. Inizia la conversazione.</p>
              )}
              {(req.comments || []).map((c, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-300 dark:bg-slate-700 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {(c.author_name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">{c.author_name}</span>
                      <Badge variant="outline" className="text-[10px]">{c.role}</Badge>
                      <span className="text-[10px] text-slate-400">{fmtDT(c.created_at)}</span>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-2.5 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-line">{c.text}</div>
                  </div>
                </div>
              ))}

              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <Textarea rows={2} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Scrivi una risposta..." data-testid="new-comment-input" />
                <div className="flex justify-end mt-2">
                  <Button onClick={postComment} disabled={saving || !newComment.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-comment">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Rispondi
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar azioni */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Azioni Stato</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-2">
              {STATUS_ACTIONS.map(a => (
                <Button
                  key={a.key}
                  disabled={saving || req.status === a.key}
                  onClick={() => changeStatus(a.key)}
                  className={`w-full ${a.color}`}
                  data-testid={`status-${a.key}`}
                >
                  {a.label}
                </Button>
              ))}
              <Button
                variant="outline"
                onClick={archive}
                disabled={saving || req.status === 'archiviata'}
                className="w-full mt-2 gap-1 text-slate-500 hover:text-slate-800"
                data-testid="archive-request"
              ><Archive className="w-4 h-4" /> Archivia</Button>
            </CardContent>
          </Card>

          {req.linked_ticket_id && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-bold uppercase tracking-wide">Ticket collegato</CardTitle></CardHeader>
              <CardContent className="p-4">
                <Link to={`/tickets/${req.linked_ticket_id}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  #{req.linked_ticket_id.slice(-6)} <ExternalLink className="w-3 h-3" />
                </Link>
              </CardContent>
            </Card>
          )}

          {req.status === 'completata' && req.closed_at && (
            <Card className="border-emerald-200/70 bg-emerald-50/40 dark:bg-emerald-900/10">
              <CardContent className="p-4 text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Completata il {fmtDT(req.closed_at)}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelRequestDetailPage;
